import { useState } from 'react';
import { Banner, Button, Input, Modal, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Pagination } from '../../components/Pagination.js';
import { useMe } from '../../hooks/useMe.js';
import { usePagination } from '../../hooks/usePagination.js';
import { isSuperAdmin } from '../../lib/roles.js';
import { SCOPE_KIND_META, scopeKindOf } from '../../lib/scopeKinds.js';
import type { Scope } from '../../lib/types.js';
import {
  useScopesLogic,
  useCreateScopeLogic,
  useUpdateScopeLogic,
  useDeleteScopeLogic,
} from './useScopesLogic.js';

const KEY_PATTERN = /^[a-zA-Z0-9._-]+$/;

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString();

const ScopesTableSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div
    className="bg-surface-elevated rounded-lg border border-outline p-4 space-y-3"
    role="status"
    aria-label="Loading scopes"
  >
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-64" />
        <div className="ml-auto flex gap-2">
          <Skeleton className="h-9 w-16" />
          <Skeleton className="h-9 w-16" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Create/edit a scope. `key` is the immutable identifier principals reference,
 * so it is editable only on create; edits are limited to display metadata.
 */
const ScopeFormModal = ({
  mode,
  scope,
  onSuccess,
  onCancel,
}: {
  mode: 'create' | 'edit';
  scope?: Scope;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const [key, setKey] = useState(scope?.key ?? '');
  const [displayName, setDisplayName] = useState(scope?.displayName ?? '');
  const [description, setDescription] = useState(scope?.description ?? '');

  const createLogic = useCreateScopeLogic(onSuccess);
  const updateLogic = useUpdateScopeLogic(onSuccess);
  const active = mode === 'create' ? createLogic : updateLogic;

  const trimmedKey = key.trim();
  const keyValid = KEY_PATTERN.test(trimmedKey);
  const canSubmit =
    !active.isSubmitting &&
    displayName.trim().length > 0 &&
    description.trim().length > 0 &&
    (mode === 'edit' || (trimmedKey.length > 0 && keyValid));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      if (mode === 'create') {
        await createLogic.create({
          key: trimmedKey,
          displayName: displayName.trim(),
          description: description.trim(),
        });
      } else if (scope) {
        await updateLogic.update(scope.id, {
          displayName: displayName.trim(),
          description: description.trim(),
        });
      }
    } catch {
      // Error is surfaced via active.error
    }
  };

  return (
    <Modal open onOpenChange={onCancel}>
      <Modal.Header>
        <Modal.Title>{mode === 'create' ? 'Create Scope' : 'Edit Scope'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {active.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{active.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}
        <div className="space-y-4">
          <div>
            <label htmlFor="scope-key" className="block text-sm font-medium text-ink mb-2">
              Key
            </label>
            <Input
              id="scope-key"
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g., Telemetry.Read"
              className="font-mono"
              disabled={mode === 'edit' || active.isSubmitting}
            />
            {mode === 'edit' ? (
              <p className="text-xs text-ink-muted mt-1">
                The key can't be changed — principals reference it directly.
              </p>
            ) : (
              trimmedKey.length > 0 &&
              !keyValid && (
                <p className="text-xs text-danger mt-1">
                  Only letters, numbers, dots, hyphens, and underscores are allowed.
                </p>
              )
            )}
          </div>
          <div>
            <label htmlFor="scope-display-name" className="block text-sm font-medium text-ink mb-2">
              Display Name
            </label>
            <Input
              id="scope-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Read telemetry"
              disabled={active.isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="scope-description" className="block text-sm font-medium text-ink mb-2">
              Description
            </label>
            <textarea
              id="scope-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this scope grants access to."
              rows={3}
              className="w-full px-3 py-2 border border-outline rounded text-sm text-ink bg-surface placeholder:text-ink-muted resize-none"
              disabled={active.isSubmitting}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={active.isSubmitting}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {active.isSubmitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? 'Create Scope'
              : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ConfirmDeleteScopeModal = ({
  scope,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  scope: Scope;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) => (
  <Modal open onOpenChange={onCancel}>
    <Modal.Header>
      <Modal.Title>Delete Scope</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="text-sm text-ink-muted">
        Delete the scope <span className="font-mono text-ink">{scope.key}</span>? Any API key or
        user currently assigned this scope will lose it. This action cannot be undone.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel} disabled={isDeleting}>
        Cancel
      </Button>
      <Button onClick={onConfirm} disabled={isDeleting}>
        {isDeleting ? 'Deleting...' : 'Delete'}
      </Button>
    </Modal.Footer>
  </Modal>
);

const PAGE_SIZE = 25;

export const ScopesPage = () => {
  const meQuery = useMe();
  const { isLoading, scopes, error } = useScopesLogic(false);
  const deleteLogic = useDeleteScopeLogic();

  // Cluster by kind (Access → Assistants → Data), then by key, so same-kind
  // scopes sit together under their badge without restructuring the table.
  const sortedScopes = [...scopes].sort((a, b) => {
    const ka = SCOPE_KIND_META[scopeKindOf(a)].order;
    const kb = SCOPE_KIND_META[scopeKindOf(b)].order;
    return ka !== kb ? ka - kb : a.key.localeCompare(b.key);
  });
  const pager = usePagination(sortedScopes, PAGE_SIZE);

  const [showCreate, setShowCreate] = useState(false);
  const [editScope, setEditScope] = useState<Scope | null>(null);
  const [deleteScope, setDeleteScope] = useState<Scope | null>(null);

  if (meQuery.isLoading || !meQuery.data) {
    return (
      <AppShell>
        <div className="mb-6">
          <Skeleton className="h-7 w-40 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <ScopesTableSkeleton rows={5} />
      </AppShell>
    );
  }

  // Catalog mutations are SUPER_ADMIN-only on the API; mirror that here so a
  // tenant admin who navigates directly doesn't hit a wall of 403s.
  if (!isSuperAdmin(meQuery.data.user.roles)) {
    return (
      <AppShell>
        <div className="text-center py-12 bg-surface-elevated rounded-lg border border-outline">
          <p className="text-ink font-semibold mb-2">Access restricted.</p>
          <p className="text-ink-muted text-sm">
            Managing the scope catalog is limited to super admins.
          </p>
        </div>
      </AppShell>
    );
  }

  const handleDeleteConfirm = async () => {
    if (!deleteScope) return;
    try {
      await deleteLogic.remove(deleteScope.id);
      setDeleteScope(null);
    } catch {
      // Error is surfaced via deleteLogic.error
    }
  };

  return (
    <AppShell fill>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">Scopes</h2>
          <p className="text-ink-muted text-sm">
            Define the API capability scopes that admins can assign to users and API keys.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ Create Scope</Button>
      </div>

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {deleteLogic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>Failed to delete scope: {deleteLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {isLoading && <ScopesTableSkeleton rows={5} />}

      {!isLoading && scopes.length === 0 && (
        <div className="text-center py-12 bg-surface-elevated rounded-lg border border-outline">
          <p className="text-ink-muted mb-4">No scopes yet. Create one to get started.</p>
        </div>
      )}

      {!isLoading && scopes.length > 0 && (
        <div className="bg-surface-elevated flex min-h-40 flex-col overflow-hidden rounded-lg border border-outline">
          <div className="min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-hover [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-surface-hover">
                <tr className="text-left text-ink-muted">
                  <th className="px-4 py-3 font-medium">Kind</th>
                  <th className="px-4 py-3 font-medium">Key</th>
                  <th className="px-4 py-3 font-medium">Display Name</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((scope) => {
                  const meta = SCOPE_KIND_META[scopeKindOf(scope)];
                  return (
                    <tr key={scope.id} className="border-t border-outline">
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${meta.badgeClass}`}
                        >
                          {meta.sectionLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-ink">{scope.key}</td>
                      <td className="px-4 py-3 text-ink">{scope.displayName}</td>
                      <td className="px-4 py-3 text-ink-muted">{scope.description}</td>
                      <td className="px-4 py-3 text-ink-muted text-xs">
                        {formatDate(scope.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditScope(scope)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setDeleteScope(scope)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            className="border-t border-outline px-4 py-3"
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
          />
        </div>
      )}

      {showCreate && (
        <ScopeFormModal
          mode="create"
          onCancel={() => setShowCreate(false)}
          onSuccess={() => setShowCreate(false)}
        />
      )}

      {editScope && (
        <ScopeFormModal
          mode="edit"
          scope={editScope}
          onCancel={() => setEditScope(null)}
          onSuccess={() => setEditScope(null)}
        />
      )}

      {deleteScope && (
        <ConfirmDeleteScopeModal
          scope={deleteScope}
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => setDeleteScope(null)}
          isDeleting={deleteLogic.isSubmitting}
        />
      )}
    </AppShell>
  );
};
